import { useState } from "react";
import { useQuery, useMutation, useApolloClient, useLazyQuery } from "@apollo/client/react";
import { GET_SOURCES, GET_SOURCE_USAGE, CREATE_SOURCE, UPDATE_SOURCE, DELETE_SOURCE } from "@/graphql/queries";
import { useApp } from "@/context/AppContext";
import type { Source } from "@/types";

interface UsageItem {
  id: string;
  title: string;
  description: string;
}

interface ConfirmState {
  action: "edit" | "delete";
  sourceId: string;
  sourceLabel: string;
  requirements: UsageItem[];
  tests: UsageItem[];
  editFields?: { label: string; url: string };
}

export function SourceManagement() {
  const { setCurrentView, activeUser } = useApp();
  const canManageSources = activeUser?.role === "admin" || activeUser?.role === "manager";
  const client = useApolloClient();
  const { data: sourcesRaw } = useQuery<any>(GET_SOURCES);
  const sources: Source[] = sourcesRaw ? sourcesRaw.sources : [];
  const [newSource, setNewSource] = useState({ id: "", label: "", url: "" });
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editFields, setEditFields] = useState({ label: "", url: "" });
  const [confirm, setConfirm] = useState<ConfirmState | null>(null);

  const [fetchUsage] = useLazyQuery<any>(GET_SOURCE_USAGE, { fetchPolicy: "network-only" });

  const [createMutation] = useMutation<any>(CREATE_SOURCE, {
    onCompleted: () => { client.refetchQueries({ include: [GET_SOURCES] }); setNewSource({ id: "", label: "", url: "" }); },
  });
  const [updateMutation] = useMutation<any>(UPDATE_SOURCE, {
    onCompleted: () => { client.refetchQueries({ include: [GET_SOURCES] }); setEditingId(null); setConfirm(null); },
  });
  const [deleteMutation] = useMutation<any>(DELETE_SOURCE, {
    onCompleted: () => { client.refetchQueries({ include: [GET_SOURCES] }); setConfirm(null); },
  });

  async function handleDelete(source: Source) {
    const { data } = await fetchUsage({ variables: { sourceId: source.id } });
    const usage = data?.sourceUsage;
    setConfirm({
      action: "delete",
      sourceId: source.id,
      sourceLabel: source.label,
      requirements: usage?.requirements || [],
      tests: usage?.tests || [],
    });
  }

  async function handleEditSave(source: Source) {
    const { data } = await fetchUsage({ variables: { sourceId: source.id } });
    const usage = data?.sourceUsage;
    setConfirm({
      action: "edit",
      sourceId: source.id,
      sourceLabel: source.label,
      requirements: usage?.requirements || [],
      tests: usage?.tests || [],
      editFields: { ...editFields },
    });
  }

  function confirmAction() {
    if (!confirm) return;
    if (confirm.action === "delete") {
      deleteMutation({ variables: { id: confirm.sourceId } });
    } else if (confirm.action === "edit" && confirm.editFields) {
      updateMutation({ variables: { id: confirm.sourceId, label: confirm.editFields.label, url: confirm.editFields.url } });
    }
  }

  return (
    <div className="flex-1 px-7 py-6 overflow-y-auto" data-testid="source-management-view">
      {/* Header */}
      <div className="flex items-center gap-3 mb-5">
        <button
          className="px-2 py-1 border border-border rounded-default bg-transparent text-text2 text-xs cursor-pointer hover:border-accent hover:text-text transition-colors"
          onClick={() => setCurrentView("process")}
          aria-label="Back to process"
        >
          &larr; Back
        </button>
        <h1 className="text-xl font-semibold text-text">Source Management</h1>
      </div>

      <p className="text-sm text-text2 mb-5">Manage the registry of source documents linked to requirements and tests. Changes affect all linked items.</p>

      {/* Add new source */}
      {canManageSources && <div className="bg-bg2 border border-border rounded-default p-4 mb-5">
        <h2 className="text-sm font-semibold text-text mb-3">Add New Source</h2>
        <div className="flex items-center gap-2">
          <input value={newSource.id} onChange={e => setNewSource({ ...newSource, id: e.target.value })} className="bg-bg border border-border rounded-default px-2 py-1.5 text-text text-xs w-32" placeholder="ID (e.g. prd-v4)" />
          <input value={newSource.label} onChange={e => setNewSource({ ...newSource, label: e.target.value })} className="bg-bg border border-border rounded-default px-2 py-1.5 text-text text-xs flex-1" placeholder="Label" />
          <input value={newSource.url} onChange={e => setNewSource({ ...newSource, url: e.target.value })} className="bg-bg border border-border rounded-default px-2 py-1.5 text-text text-xs flex-1" placeholder="URL (optional)" />
          <button
            className="px-3 py-1.5 border border-accent rounded-default bg-accent text-white text-xs cursor-pointer font-semibold hover:bg-accent2 hover:border-accent2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={!newSource.id || !newSource.label}
            onClick={() => createMutation({ variables: { input: { id: newSource.id, label: newSource.label, url: newSource.url || null } } })}
          >Add Source</button>
        </div>
      </div>}

      {/* Sources table */}
      <div className="bg-bg2 border border-border rounded-default overflow-hidden">
        <table className="w-full border-collapse text-xs table-fixed" data-testid="sources-table">
          <thead>
            <tr className="border-b border-border">
              <th className="px-4 py-2.5 text-left font-semibold text-[10px] uppercase tracking-wide text-text2 w-32">ID</th>
              <th className="px-4 py-2.5 text-left font-semibold text-[10px] uppercase tracking-wide text-text2">Label</th>
              <th className="px-4 py-2.5 text-left font-semibold text-[10px] uppercase tracking-wide text-text2 max-w-[300px]">URL</th>
              <th className="px-4 py-2.5 text-right font-semibold text-[10px] uppercase tracking-wide text-text2 w-32 shrink-0">{canManageSources ? "Actions" : ""}</th>
            </tr>
          </thead>
          <tbody>
            {sources.map(s => (
              <tr key={s.id} className="border-b border-border/50 hover:bg-bg3 transition-colors">
                {editingId === s.id ? (
                  <>
                    <td className="px-4 py-2.5 text-accent font-semibold">{s.id}</td>
                    <td className="px-4 py-2">
                      <input value={editFields.label} onChange={e => setEditFields({ ...editFields, label: e.target.value })} className="w-full bg-bg border border-border rounded-default px-2 py-1 text-text text-xs" />
                    </td>
                    <td className="px-4 py-2">
                      <input value={editFields.url} onChange={e => setEditFields({ ...editFields, url: e.target.value })} className="w-full bg-bg border border-border rounded-default px-2 py-1 text-text text-xs" placeholder="(optional)" />
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      <div className="flex items-center gap-2 justify-end">
                        <button className="text-accent text-xs bg-transparent border-none cursor-pointer hover:underline" onClick={() => handleEditSave(s)}>Save</button>
                        <button className="text-text2 text-xs bg-transparent border-none cursor-pointer hover:underline" onClick={() => setEditingId(null)}>Cancel</button>
                      </div>
                    </td>
                  </>
                ) : (
                  <>
                    <td className="px-4 py-2.5 text-accent font-semibold">{s.id}</td>
                    <td className="px-4 py-2.5 text-text">{s.label}</td>
                    <td className="px-4 py-2.5 max-w-[300px]">
                      {s.url ? <a href={s.url} target="_blank" rel="noopener noreferrer" className="text-accent hover:underline block truncate">{s.url}</a> : <span className="text-text2">—</span>}
                    </td>
                    <td className="px-4 py-2.5 text-right w-32">
                      {canManageSources && <div className="flex items-center gap-2 justify-end">
                        <button className="text-accent text-xs bg-transparent border-none cursor-pointer hover:underline" onClick={() => { setEditingId(s.id); setEditFields({ label: s.label, url: s.url || "" }); }}>Edit</button>
                        <button className="text-red text-xs bg-transparent border-none cursor-pointer hover:underline" onClick={() => handleDelete(s)}>Delete</button>
                      </div>}
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
        {sources.length === 0 && (
          <div className="text-center p-8 text-text2">No sources registered yet. Add one above.</div>
        )}
      </div>

      {/* Confirmation dialog */}
      {confirm && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" data-testid="confirm-dialog">
          <div className="bg-bg2 border border-border rounded-default p-6 max-w-lg w-full mx-4 shadow-xl">
            <h3 className="text-sm font-semibold text-text mb-3">
              {confirm.action === "delete" ? "Confirm Delete" : "Confirm Edit"}
            </h3>
            <p className="text-xs text-text2 mb-3">
              {confirm.action === "delete"
                ? `Are you sure you want to delete source "${confirm.sourceLabel}"? This will unlink it from all items below.`
                : `Are you sure you want to edit source "${confirm.sourceLabel}"? This change affects all items below.`}
            </p>

            {(confirm.requirements.length > 0 || confirm.tests.length > 0) ? (
              <div className="max-h-48 overflow-y-auto mb-4 border border-border rounded-default p-3 bg-bg">
                {confirm.requirements.length > 0 && (
                  <div className="mb-2">
                    <div className="text-[10px] uppercase tracking-wide text-text2 font-semibold mb-1">Requirements ({confirm.requirements.length})</div>
                    {confirm.requirements.map(r => (
                      <div key={r.id} className="text-xs text-text py-0.5">
                        <span className="text-accent font-semibold">{r.id}</span>
                        <span className="text-text2 ml-2">{r.title || r.description}</span>
                      </div>
                    ))}
                  </div>
                )}
                {confirm.tests.length > 0 && (
                  <div>
                    <div className="text-[10px] uppercase tracking-wide text-text2 font-semibold mb-1">Tests ({confirm.tests.length})</div>
                    {confirm.tests.map(t => (
                      <div key={t.id} className="text-xs text-text py-0.5">
                        <span className="text-accent font-semibold">{t.id}</span>
                        <span className="text-text2 ml-2">{t.title || t.description}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <p className="text-xs text-text2 mb-4 italic">No requirements or tests are currently linked to this source.</p>
            )}

            <div className="flex items-center gap-2 justify-end">
              <button
                className="px-3 py-1.5 border border-border rounded-default bg-transparent text-text2 text-xs cursor-pointer hover:border-accent hover:text-text transition-colors"
                onClick={() => setConfirm(null)}
              >Cancel</button>
              <button
                className={`px-3 py-1.5 border rounded-default text-white text-xs cursor-pointer font-semibold transition-colors ${confirm.action === "delete" ? "border-red bg-red hover:bg-red/80" : "border-accent bg-accent hover:bg-accent2"}`}
                onClick={confirmAction}
              >
                {confirm.action === "delete" ? "Delete" : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
